import { CHUNK_RELOAD_KEY, CHUNK_RELOAD_WINDOW_MS } from './update-manager'
import type { CheckResult } from './version'

/**
 * Recovery from a lazy chunk that no longer loads (old build after a deploy). Order:
 * newer build published or the chunk URL answers 404 -> save drafts and reload ONCE (loop guard in sessionStorage);
 * otherwise (probably just a network blip) show a retry state. Drafts are saved first; if saving fails we
 * do not reload by ourselves.
 */

const CHUNK_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk [\w-]+ failed/i

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? `${error.name} ${error.message}` : typeof error === 'string' ? error : ''
  return CHUNK_MESSAGE.test(message)
}

function chunkUrl(error: unknown): string | null {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return /(https?:\/\/[^\s'")]+)/.exec(message)?.[1] ?? null
}

export type ChunkOutcome = 'reloaded' | 'retry' | 'ignored'

export interface ChunkDeps {
  checkNow: () => Promise<CheckResult | null>
  flushDrafts: () => Promise<boolean>
  setChunkError: (value: boolean) => void
  storage: Pick<Storage, 'getItem' | 'setItem'>
  reload: () => void
  fetchImpl?: typeof fetch
  now?: () => number
}

export function createChunkErrorHandler(deps: ChunkDeps) {
  const { checkNow, flushDrafts, setChunkError, storage, reload, now = Date.now } = deps
  const fetchImpl = deps.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args))
  let handling: Promise<ChunkOutcome> | null = null

  function recentlyReloaded(): boolean {
    try {
      const at = Number(storage.getItem(CHUNK_RELOAD_KEY))
      return Number.isFinite(at) && at > 0 && now() - at < CHUNK_RELOAD_WINDOW_MS
    } catch {
      return false
    }
  }

  async function chunkIs404(error: unknown): Promise<boolean> {
    const url = chunkUrl(error)
    if (!url) return false
    try {
      const response = await fetchImpl(url, { method: 'HEAD', cache: 'no-store' })
      return response.status === 404
    } catch {
      return false
    }
  }

  async function run(error: unknown): Promise<ChunkOutcome> {
    if (!isChunkLoadError(error)) return 'ignored'
    if (recentlyReloaded()) {
      setChunkError(true) // already reloaded once for this: no loop, let the person retry
      return 'retry'
    }
    const [result, notFound] = await Promise.all([checkNow(), chunkIs404(error)])
    if (!(result?.buildChanged || notFound)) {
      setChunkError(true)
      return 'retry'
    }
    if (!(await flushDrafts())) {
      setChunkError(true) // could not keep the draft: let the person decide
      return 'retry'
    }
    try {
      storage.setItem(CHUNK_RELOAD_KEY, String(now()))
    } catch {
      /* private mode */
    }
    reload()
    return 'reloaded'
  }

  return {
    handle(error: unknown): Promise<ChunkOutcome> {
      handling ??= run(error).finally(() => {
        handling = null
      })
      return handling
    },
    /** "Try again": drafts first, then a plain reload (explicit user action, so it may repeat). */
    async retry() {
      await flushDrafts()
      reload()
    },
  }
}

export type ChunkErrorHandler = ReturnType<typeof createChunkErrorHandler>

/** Hooks the browser-level signals. Returns a disposer. */
export function installChunkErrorListeners(handler: Pick<ChunkErrorHandler, 'handle'>): () => void {
  const onPreload = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    if (!isChunkLoadError(payload)) return
    // Not prevented on purpose: Vite then rethrows, so the route/lazy caller sees the real error (not `undefined`).
    void handler.handle(payload)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) void handler.handle(event.reason)
  }
  window.addEventListener('vite:preloadError', onPreload)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('vite:preloadError', onPreload)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}
